import React from 'react';
import {
  Checkbox,
  Label,
  Separator,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@roger/ui-components';
import { Stethoscope } from 'lucide-react';
import type { DisciplineType } from '../../services/api/models/DisciplineType';
import { CLINICIAN_DISCIPLINE_OPTIONS } from './disciplineConstants';
import type { ClinicDisciplineRow } from './useClinicDisciplines';

const DISCIPLINE_COL_CLASS = 'w-14 px-1 text-center align-middle';

export type ClinicDisciplinesControl = {
  rows: ClinicDisciplineRow[];
  onToggleDiscipline: (clinicId: string, discipline: DisciplineType) => void;
  onSelectAllDisciplines: () => void;
  onClearAllDisciplines: () => void;
  onSelectAllForClinic: (clinicId: string) => void;
  onClearForClinic: (clinicId: string) => void;
};

type ClinicDisciplinesSectionProps = ClinicDisciplinesControl & {
  /** Render inside the Assignable as Clinician role tile (no outer heading/separator). */
  embedded?: boolean;
};

export const ClinicDisciplinesSection: React.FC<ClinicDisciplinesSectionProps> =
  React.memo(function ClinicDisciplinesSection({
    rows,
    onToggleDiscipline,
    onSelectAllDisciplines,
    onClearAllDisciplines,
    onSelectAllForClinic,
    onClearForClinic,
    embedded = false,
  }) {
    const showGlobalActions = rows.length > 1;
    const allRowsFullySelected =
      rows.length > 0 &&
      rows.every(
        row => row.disciplines.size === CLINICIAN_DISCIPLINE_OPTIONS.length
      );

    if (embedded && rows.length === 0) {
      return null;
    }

    const table = (
      <div className={embedded ? 'overflow-x-auto' : 'rounded-md border'}>
        <Table className='table-fixed'>
          <TableHeader>
            <TableRow>
              <TableHead className='h-8 py-1.5 align-middle text-xs'>
                Clinic
              </TableHead>
              {CLINICIAN_DISCIPLINE_OPTIONS.map(option => (
                <TableHead
                  key={option.value}
                  className={`h-8 py-1.5 text-xs ${DISCIPLINE_COL_CLASS}`}
                >
                  {option.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(row => {
              const allSelected =
                row.disciplines.size === CLINICIAN_DISCIPLINE_OPTIONS.length;

              return (
                <TableRow key={row.clinic_id}>
                  <TableCell className='py-1.5 align-middle'>
                    <div className='flex items-center justify-between gap-2 min-w-0'>
                      <Label className='min-w-0 truncate text-sm font-medium'>
                        {row.clinic_name}
                      </Label>
                      <button
                        type='button'
                        onClick={() =>
                          allSelected
                            ? onClearForClinic(row.clinic_id)
                            : onSelectAllForClinic(row.clinic_id)
                        }
                        className='flex-shrink-0 rounded px-0.5 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                      >
                        {allSelected ? 'Clear' : 'Select all'}
                      </button>
                    </div>
                  </TableCell>
                  {CLINICIAN_DISCIPLINE_OPTIONS.map(option => (
                    <TableCell
                      key={option.value}
                      className={`py-1.5 ${DISCIPLINE_COL_CLASS}`}
                    >
                      <div className='flex items-center justify-center'>
                        <Checkbox
                          checkboxSize='sm'
                          checked={row.disciplines.has(option.value)}
                          onCheckedChange={() =>
                            onToggleDiscipline(row.clinic_id, option.value)
                          }
                        />
                      </div>
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );

    if (embedded) {
      return (
        <div className='space-y-2'>
          <div className='flex items-center justify-between gap-3'>
            <p className='text-xs font-medium text-muted-foreground'>
              Disciplines
            </p>
            {showGlobalActions && (
              <div className='flex flex-shrink-0 items-center gap-2'>
                {!allRowsFullySelected && (
                  <>
                    <button
                      type='button'
                      onClick={onSelectAllDisciplines}
                      className='rounded px-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                    >
                      Select all
                    </button>
                    <span className='text-xs text-muted-foreground'>·</span>
                  </>
                )}
                <button
                  type='button'
                  onClick={onClearAllDisciplines}
                  className='rounded px-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                >
                  Clear all
                </button>
              </div>
            )}
          </div>
          {table}
        </div>
      );
    }

    return (
      <div>
        <div className='space-y-3'>
          <div className='flex items-center justify-between gap-3'>
            <div className='flex items-center gap-2'>
              <Stethoscope className='h-4 w-4 text-muted-foreground' />
              <h3 className='text-sm font-semibold'>Clinic Disciplines</h3>
            </div>
            {showGlobalActions && (
              <div className='flex flex-shrink-0 items-center gap-2'>
                {!allRowsFullySelected && (
                  <>
                    <button
                      type='button'
                      onClick={onSelectAllDisciplines}
                      className='rounded px-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                    >
                      Select all
                    </button>
                    <span className='text-xs text-muted-foreground'>·</span>
                  </>
                )}
                <button
                  type='button'
                  onClick={onClearAllDisciplines}
                  className='rounded px-1 text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                >
                  Clear all
                </button>
              </div>
            )}
          </div>

          {rows.length === 0 ? (
            <p className='text-sm text-muted-foreground'>
              Assign this user as a clinician at a clinic first.
            </p>
          ) : (
            table
          )}
        </div>
        <Separator className='my-4' />
      </div>
    );
  });
